import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage, ScenarioConfig, UserProfile } from '../types';
import { initializeChatSession, sendMessageToGemini } from '../services/geminiService';
import { AdBanner } from './AdBanner';
import { Button } from './Button';

interface ChatInterfaceProps {
  user: UserProfile;
  scenario: ScenarioConfig;
  initialMessages?: ChatMessage[];
  onUpdateChat: (messages: ChatMessage[], averageScore: number) => void;
  onGuestSignupPrompt?: () => void; // Callback when guest limit reached
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
  user, 
  scenario, 
  initialMessages = [], 
  onUpdateChat,
  onGuestSignupPrompt
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);

  // Guest Limit Logic
  const GUEST_MESSAGE_LIMIT = 5;
  const userMessageCount = messages.filter(m => m.sender === 'user').length;
  const isGuestLimitReached = user.isGuest && userMessageCount >= GUEST_MESSAGE_LIMIT;

  // Initialize Chat Session
  useEffect(() => {
    // We only initialize the AI session once when the component mounts
    if (!initialized.current) {
        initializeChatSession(user, scenario, initialMessages);
        initialized.current = true;

        // Only trigger the "intro" sequence if it's a fresh chat
        if (initialMessages.length === 0) {
            const initialGreeting: ChatMessage = {
                id: 'init',
                sender: 'ai',
                text: "Online",
                timestamp: Date.now()
            };
            
            setTimeout(() => {
                const startMsg: ChatMessage = {
                    id: Date.now().toString(),
                    sender: 'ai',
                    text: "Hey.",
                    timestamp: Date.now()
                };
                setMessages([startMsg]);
                // Initial update with 0 score
                onUpdateChat([startMsg], 0);
            }, 1000);
        } else {
            // If resuming, scroll to bottom
            setTimeout(scrollToBottom, 100);
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const calculateAverageScore = (msgs: ChatMessage[]) => {
    const scoredMessages = msgs.filter(m => m.sender === 'ai' && m.score !== undefined);
    if (scoredMessages.length === 0) return 0;
    const total = scoredMessages.reduce((sum, m) => sum + (m.score || 0), 0);
    return parseFloat((total / scoredMessages.length).toFixed(1));
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;

    // Double check limit before sending
    if (isGuestLimitReached) {
        if (onGuestSignupPrompt) onGuestSignupPrompt();
        return;
    }

    const userMsgId = Date.now().toString();
    const newUserMsg: ChatMessage = {
      id: userMsgId,
      sender: 'user',
      text: inputText,
      timestamp: Date.now()
    };

    const newMessagesLocal = [...messages, newUserMsg];
    setMessages(newMessagesLocal);
    setInputText('');
    setIsTyping(true);

    // Save state immediately with user message
    onUpdateChat(newMessagesLocal, calculateAverageScore(newMessagesLocal));

    try {
      const response = await sendMessageToGemini(newUserMsg.text);
      
      const aiMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: response.personaResponse,
        timestamp: Date.now(),
        coachingTip: response.coachingTip,
        score: response.score
      };

      const finalMessages = [...newMessagesLocal, aiMsg];
      setMessages(finalMessages);
      onUpdateChat(finalMessages, calculateAverageScore(finalMessages));
    } catch (error) {
      console.error("Failed to send", error);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 relative">
      <AdBanner position="top" />

      {/* Messages Area - min-h-0 is critical for nested flex scrolling */}
      <div className="flex-1 overflow-y-auto min-h-0 px-4 py-4 space-y-6 scrollbar-hide touch-pan-y" style={{ WebkitOverflowScrolling: 'touch' }}>
        {messages.filter(m => m.id !== 'init').map((msg) => (
            <div key={msg.id} className="space-y-2">
                
                {/* Message Bubble */}
                <div className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div 
                        className={`max-w-[85%] px-5 py-3 rounded-2xl text-sm leading-relaxed shadow-sm font-medium ${
                        msg.sender === 'user' 
                            ? 'bg-brand-700 text-white rounded-tr-none' 
                            : 'bg-white text-slate-800 border border-slate-200 rounded-tl-none'
                        }`}
                    >
                        {msg.text}
                    </div>
                </div>

                {/* Coaching Feedback */}
                {msg.sender === 'ai' && msg.coachingTip && (
                    <div className="animate-fade-in mx-auto max-w-[95%] bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex gap-3 items-start shadow-sm">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            (msg.score || 0) >= 7 ? 'bg-green-100 text-green-700' : 
                            (msg.score || 0) >= 4 ? 'bg-amber-100 text-amber-700' : 
                            'bg-red-100 text-red-700'
                        }`}>
                            {msg.score}/10
                        </div>
                        <div className="flex-1">
                            <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wide mb-1 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                Coach's Analysis
                            </h4>
                            <p className="text-xs text-indigo-900 leading-relaxed font-medium">
                                {msg.coachingTip}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none px-4 py-3 flex items-center gap-1 shadow-sm">
              <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Guest Limit Overlay */}
      {isGuestLimitReached && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-30 flex items-end justify-center pb-safe">
            <div className="bg-white m-4 p-6 rounded-2xl shadow-2xl border border-brand-100 w-full max-w-sm mb-20 animate-fade-in-up">
                <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mb-4 mx-auto text-2xl">🔒</div>
                <h3 className="text-xl font-bold text-slate-900 text-center mb-2">Continue this chat?</h3>
                <p className="text-slate-500 text-center text-sm mb-6">
                    You've reached the free guest limit. Create a free account to unlock more messages and save your progress.
                </p>
                <Button onClick={onGuestSignupPrompt} fullWidth>
                    Save Chat & Create Account
                </Button>
            </div>
        </div>
      )}

      {/* Input Area - Flex-none ensures it stays at bottom */}
      <div 
        className="bg-white border-t border-slate-200 pt-3 px-4 flex-none z-10 relative"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}
      >
        <div className="flex items-end gap-2 max-w-4xl mx-auto">
          <div className="flex-1 relative">
             <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={isGuestLimitReached ? "Sign up to continue typing..." : "Type your message..."}
              disabled={isGuestLimitReached}
              className="w-full bg-slate-800 text-white placeholder-slate-400 border border-slate-700 rounded-2xl px-4 py-3 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none resize-none text-sm max-h-32 shadow-sm disabled:opacity-50 pr-10"
              rows={1}
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim() || isTyping || isGuestLimitReached}
            className="w-11 h-11 flex items-center justify-center rounded-full bg-brand-600 text-white shadow-md hover:bg-brand-700 disabled:opacity-50 disabled:shadow-none transition-all flex-shrink-0"
          >
            <svg className="w-5 h-5 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <div className="text-center mt-2 flex justify-between px-2">
            <span className="text-[10px] text-slate-400">AI can make mistakes.</span>
            {user.isGuest && (
                <span className="text-[10px] text-brand-600 font-bold">
                    Guest Mode: {Math.max(0, GUEST_MESSAGE_LIMIT - userMessageCount)} msgs left
                </span>
            )}
        </div>
      </div>
    </div>
  );
};